<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { portalAction } from '$lib/directives/portalAction';
  import { dialogs } from './stores';
  import { DialogStatus } from './types';
  import { beforeNavigate } from '$app/navigation';

  const dispatch = createEventDispatcher();

  export let id = '';
  export let dialogEl: HTMLDialogElement | undefined = undefined;
  export let status: DialogStatus = DialogStatus.Closed;
  export let allowClickOutside = true;
  export let allowCloseOnNavigation = true;
  export let onOpenDelay = 0; // ms
  export let onCloseDelay = 0; // ms

  let openTimerId: NodeJS.Timeout;
  let closeTimerId: NodeJS.Timeout;

  // We only trigger showModal() becuase closing is handled by the dialog itself
  export let isOpen = false;

  $: if (dialogEl && isOpen) {
    status = DialogStatus.Open;
    dispatch('open');
    dialogEl.showModal();
    clearTimeout(openTimerId);
    openTimerId = setTimeout(() => {
      dialogs.update(dialogEls => [...dialogEls, dialogEl!]);
      dispatch('opened');
    }, onOpenDelay);
  }

  $: if (dialogEl && !isOpen && status === DialogStatus.Open) {
    closeModal();
  }

  export let className = '';
  export { className as class };
  export let style: string | null = null;

  export async function closeModal() {
    return new Promise<void>(resolve => {
      status = DialogStatus.Closing;
      dispatch('closing');
      dialogs.update(dialogEls => {
        return dialogEls.filter(el => el !== dialogEl);
      });
      clearTimeout(closeTimerId);
      clearTimeout(openTimerId);
      closeTimerId = setTimeout(() => {
        dispatch('closed');
        isOpen = false;
        dialogEl?.close();
        resolve();
      }, onCloseDelay);
    });
  }

  function setClosed(event: Event) {
    if (!allowClickOutside && event.target === dialogEl && event.type === 'close') {
      // Chrome is going to close the dialog when pressing escape, no matter what
      // due to this bug;  https://issues.chromium.org/issues/41491338#comment6 ... so we will
      // just have to comply and fire the close event ourselves.
      closeModal();
    }
    status = DialogStatus.Closed;
  }

  function cancelDialog(event: Event) {
    if (event.target !== dialogEl) {
      return;
    }
    dispatch('clickOutside');
    if (allowClickOutside) {
      closeModal();
    }
  }

  // Close dialog when navigating to a different page
  beforeNavigate(() => {
    if (isOpen && allowCloseOnNavigation) closeModal();
  });

  onDestroy(() => {
    clearTimeout(openTimerId);
    clearTimeout(closeTimerId);
  });

  const baseClasses =
    'm-0 bg-transparent text-[inherit] backdrop:transition-[opacity,background-color] will-change-[opacity,background-color]';
  const focusClasses =
    'outline-none [&:not(:focus-within):not(:hover)]:focus-visible:ring-2 [&:not(:focus-within):not(:hover)]:focus-visible:ring-ondark-focused ring-inset ring-offset-4 ring-offset-ondark-base';
  const dialogTransitionClasses: {
    [key in DialogStatus]: string;
  } = {
    open: 'backdrop:duration-300 backdrop:bg-neutral-950/50 backdrop:opacity-1',
    closing: 'backdrop:duration-300 backdrop:bg-transparent backdrop:opacity-0 ease-expo-in',
    closed: 'backdrop:duration-100 backdrop:bg-transparent backdrop:opacity-0'
  };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
<dialog
  bind:this={dialogEl}
  data-id={id}
  data-is-top={$dialogs[$dialogs.length - 1] === dialogEl}
  data-status={status}
  class="{baseClasses} {focusClasses} {dialogTransitionClasses[status]} {className} touch-none"
  {style}
  use:portalAction={'body'}
  on:close={setClosed}
  on:cancel|preventDefault={cancelDialog}
  on:pointerdown|self|preventDefault={cancelDialog}
>
  <slot />
</dialog>
